"use client";

import { useState } from "react";
import Link from "next/link";

function readSanityToken(projectId: string | undefined): string | null {
  if (typeof window === "undefined") return null;
  try {
    const specific = projectId ? window.localStorage.getItem(`__studio_auth_token_${projectId}`) : null;
    if (specific) {
      const parsed = JSON.parse(specific) as { token?: string };
      if (parsed?.token) return parsed.token;
    }
    for (let i = 0; i < window.localStorage.length; i++) {
      const key = window.localStorage.key(i);
      if (!key || !key.startsWith("__studio_auth_token_")) continue;
      const raw = window.localStorage.getItem(key);
      if (!raw) continue;
      const parsed = JSON.parse(raw) as { token?: string };
      if (parsed?.token) return parsed.token;
    }
  } catch {
    // ignore
  }
  return null;
}

export function OwnerActivator() {
  const [status, setStatus] = useState<"idle" | "loading" | "missing-token" | "unauthorized" | "error">("idle");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const activate = async () => {
    setStatus("loading");
    setErrorMessage(null);

    const projectId = process.env.NEXT_PUBLIC_SANITY_PROJECT_ID;
    const token = readSanityToken(projectId);
    if (!token) {
      setStatus("missing-token");
      return;
    }

    try {
      const response = await fetch("/api/owner/enable", {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.status === 401) {
        setStatus("unauthorized");
        return;
      }
      if (!response.ok) {
        const data = (await response.json().catch(() => null)) as { error?: string } | null;
        setStatus("error");
        setErrorMessage(data?.error ?? "Activation failed.");
        return;
      }
      window.location.href = "/";
    } catch (error) {
      console.error("[Owner] Failed to activate", error);
      setStatus("error");
      setErrorMessage("Activation failed. Please try again.");
    }
  };

  if (status === "missing-token" || status === "unauthorized") {
    return (
      <div className="space-y-4">
        <p className="text-sm text-slate-700">
          We couldn&apos;t verify your Studio session. Please sign in to the Studio and come back.
        </p>
        {/* Full document load, not <Link>: Studio is a separate SPA that
            bootstraps on a real navigation, and proxy.ts sets the
            studio_access cookie on the document request. */}
        {/* eslint-disable-next-line @next/next/no-html-link-for-pages */}
        <a
          href="/studio"
          className="inline-flex items-center justify-center rounded-sm bg-[var(--primary)] px-6 py-3 text-xs font-semibold uppercase tracking-[0.3em] text-white transition hover:-translate-y-0.5"
        >
          Open Studio
        </a>
        <div>
          <button
            type="button"
            onClick={() => {
              setStatus("idle");
              setErrorMessage(null);
            }}
            className="mt-2 text-xs uppercase tracking-[0.3em] text-slate-500 underline-offset-4 hover:underline"
          >
            Try again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <button
        type="button"
        onClick={activate}
        disabled={status === "loading"}
        className="inline-flex items-center justify-center rounded-sm bg-[var(--primary)] px-6 py-3 text-xs font-semibold uppercase tracking-[0.3em] text-white transition hover:-translate-y-0.5 disabled:cursor-not-allowed disabled:opacity-60"
      >
        {status === "loading" ? "Activating…" : "Enter owner view"}
      </button>
      {errorMessage ? (
        <p className="text-xs text-red-600">{errorMessage}</p>
      ) : null}
      <p className="text-xs text-slate-500">
        <Link href="/" className="underline-offset-4 hover:underline">
          Cancel
        </Link>
      </p>
    </div>
  );
}
